import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { MultiCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import { EventService, LogoutEvent } from '@spartacus/core';
import { combineLatest, finalize, Observable, switchMap } from 'rxjs';
import { first, map, take, tap } from 'rxjs/operators';

import { DpBaseSiteService } from '@app/core/services/base-site.service';
import { DpGtmService } from '@app/core/services/gtm.service';
import { DpCart } from '@app/core/types/view/dp-cart.interface';
import { DpCartService } from '@app-modules/cart/shared';
import { DpCartApiService } from '@app-modules/cart/shared/services/cart-api.service';
import { CartAdditionalFieldsActions } from '@app-modules/cart/store/cart-additional-fields.actions';
import { CartAdditionalFieldValue } from '@app-shared/cart/types/cart-additional-field-value.type';
import { CartAdditionalField } from '@app-shared/cart/types/cart-additional-field.type';
import { GoogleAnalyticsItem } from '@app-shared/google-tag-manager';

import {
  additionalFieldsSelector,
  entriesValuesSelector,
  expandedAdditionalFieldsSelector,
  featureSelector,
  isOneShippingDeliverySelector,
  selectedEntriesSelector
} from '../../store/cart-additional-fields.selectors';

@Injectable({ providedIn: 'root' })
export class DpAdditionalInfoService {
  readonly #cartService = inject(DpCartService);
  readonly #baseService = inject(DpBaseSiteService);
  readonly #store = inject(Store);
  readonly #eventService = inject(EventService);
  readonly #cartApiService = inject(DpCartApiService);
  readonly #multiCartFacade = inject(MultiCartFacade);
  readonly #gtmService = inject(DpGtmService);

  constructor() {
    this.#eventService
      .get(LogoutEvent)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.#store.dispatch(CartAdditionalFieldsActions.resetFields()));
  }

  get fields$(): Observable<CartAdditionalField[]> {
    return this.#store.select(additionalFieldsSelector);
  }

  get expanded$(): Observable<boolean> {
    return this.#store.select(expandedAdditionalFieldsSelector);
  }

  get entriesValues$(): Observable<CartAdditionalFieldValue[]> {
    return this.#store.select(entriesValuesSelector);
  }

  get selectedEntries$(): Observable<number[]> {
    return this.#store.select(selectedEntriesSelector);
  }

  get isOneShippingDelivery$(): Observable<boolean> {
    return this.#store.select(isOneShippingDeliverySelector);
  }

  getEntryValue$(entryNumber: number): Observable<CartAdditionalFieldValue> {
    return this.entriesValues$.pipe(map((entriesValues: CartAdditionalFieldValue[]) => entriesValues[entryNumber]));
  }

  initializeFields(cartValidator?: (cart: DpCart) => boolean | undefined): void {
    combineLatest([this.#cartService.getActive(), this.#baseService.getDefault()])
      .pipe(first(([cart]) => !!cart.code && !!(cartValidator ? cartValidator(cart) : true)))
      .subscribe(([cart, baseSite]) => {
        this.#store.dispatch(
          CartAdditionalFieldsActions.initializeFields({
            payload: {
              additionalFields: baseSite?.baseStore?.orderEntryAdditionalFields || [],
              entries: cart.entries ?? [],
              completeDelivery: cart.completeDelivery
            }
          })
        );
      });
  }

  toggleExpandFields(expanded: boolean): void {
    this.#store.dispatch(CartAdditionalFieldsActions.toggleFieldsVisibility({ expanded }));
  }

  toggleOneShippingDelivery(isOneShippingDelivery: boolean, cart = ''): void {
    this.#cartApiService
      .saveAdditionalFields(cart, { completeDelivery: isOneShippingDelivery, entries: [] })
      .pipe(take(1))
      .subscribe(() =>
        this.#store.dispatch(CartAdditionalFieldsActions.toggleOneShippingDelivery({ isOneShippingDelivery }))
      );
  }

  updateEntriesValues(entryNumbers: (number | undefined)[]): void {
    this.#store.dispatch(CartAdditionalFieldsActions.resetFields());
    const isValid = (cart: DpCart): boolean | undefined =>
      cart?.entries?.some((entry) => entryNumbers?.some((entryNumber) => entryNumber === entry?.entryNumber));
    this.initializeFields(isValid);
  }

  updateEntryValue(entryNumber: number, label: string, value: string): void {
    this.#store.dispatch(CartAdditionalFieldsActions.updateEntryValue({ payload: { entryNumber, label, value } }));
  }

  removeEntryValues(entryNumber: number): void {
    this.#store.dispatch(CartAdditionalFieldsActions.removeEntryValues({ entryNumber }));
  }

  toggleSelectAllEntries(checked: boolean): void {
    if (checked) {
      this.#cartService
        .getEntries()
        .pipe(take(1))
        .pipe(map((entries) => entries.map((entry) => entry?.entryNumber ?? 0)))
        .subscribe((selectedEntries: number[]) =>
          this.#store.dispatch(CartAdditionalFieldsActions.selectAllEntries({ selectedEntries }))
        );
    } else {
      this.#store.dispatch(CartAdditionalFieldsActions.unSelectAllEntries());
    }
  }

  toggleSelectEntry(checked: boolean, entryNumber = 0): void {
    if (checked) {
      this.#store.dispatch(CartAdditionalFieldsActions.selectEntry({ entryNumber }));
    } else {
      this.#store.dispatch(CartAdditionalFieldsActions.unselectEntry({ entryNumber }));
    }
  }

  saveCartAdditionalField(cart: string): void {
    this.#store
      .select(featureSelector)
      .pipe(
        take(1),
        switchMap(({ entriesValues, isOneShippingDelivery }) => {
          const entries = entriesValues.reduce(
            (acc: CartAdditionalFieldValue[], field: CartAdditionalFieldValue, entryNumber: number) => {
              delete field.contractCode;
              delete Object.assign(field, { entryOpenText: field.openText }).openText;
              field.connectCode = !isOneShippingDelivery
                ? field.connectCode
                  ? parseInt(String(field.connectCode), 10)
                  : null
                : null;
              return [...acc, { ...field, entryNumber }];
            },
            []
          );
          return this.#cartApiService
            .saveAdditionalFields(cart, { completeDelivery: isOneShippingDelivery, entries })
            .pipe(
              take(1),
              tap(() => {
                this.#multiCartFacade.reloadCart(cart);
              }),
              finalize(() => this.#store.dispatch(CartAdditionalFieldsActions.resetFields()))
            );
        })
      )
      .subscribe();
  }

  removeSelectedEntries(): void {
    combineLatest([this.selectedEntries$, this.#cartService.getEntries()])
      .pipe(
        take(1),
        switchMap(([selectedEntryNumbers, entries]: [number[], OrderEntry[]]) => {
          const selectedEntries = entries.filter((entry) => selectedEntryNumbers.includes(entry?.entryNumber ?? 0));
          const items = selectedEntries.reduce(
            (acc: GoogleAnalyticsItem[], entry: OrderEntry) => [
              ...acc,
              {
                item_id: entry.product?.code,
                item_name: entry.product?.name,
                currency: entry.totalPrice?.currencyIso,
                item_brand: entry.product?.brandName,
                price: entry.totalPrice?.value,
                quantity: entry.quantity
              }
            ],
            []
          );
          this.#gtmService.pushTag('remove_from_cart', items).then(() => {
            items.forEach((item) => {
              this.#gtmService.eraseCategoriesFromLocalStorage(item.item_id);
            });
          });
          return this.#cartService.clearCurrentCart(selectedEntryNumbers).pipe(take(1));
        })
      )
      .subscribe(() => {
        this.toggleSelectAllEntries(false);
        this.#store.dispatch(CartAdditionalFieldsActions.clearEntriesValues());
      });
  }
}
